import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import { authService } from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import {
  exportStatewideKpis,
  getStatewideKpis,
  StatewideKpisData,
} from '../../services/statewideAnalyticsService';
import { openAnalyticsPrintWindow } from '../../utils/analyticsExport';
import { localTodayIso } from '../../utils/billingReconciliationExport';
import KpiCardGrid, { KpiCardItem } from '../../components/analytics/KpiCardGrid';
import AnalyticsLineChart from '../../components/analytics/AnalyticsLineChart';
import AnalyticsVerticalBarChart from '../../components/analytics/AnalyticsVerticalBarChart';
import FacilityPerformanceTable from '../../components/analytics/FacilityPerformanceTable';
import AnalyticsExportMenu, {
  AnalyticsExportFormat,
} from '../../components/analytics/AnalyticsExportMenu';
import AnalyticsDashboardSkeleton from '../../components/analytics/AnalyticsDashboardSkeleton';
import {
  formatDurationMinutes,
  formatNumber,
  formatPercent,
} from '../../components/analytics/chartUtils';
import '../../components/analytics/analytics-theme.css';

function defaultFromDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().slice(0, 10);
}

const TREND_METRIC_OPTIONS = [
  { value: 'studies_performed', label: 'Studies performed' },
  { value: 'adoption', label: 'Adoption %' },
  { value: 'sla_compliance', label: 'SLA compliance %' },
] as const;

const TREND_METRIC_LABELS: Record<string, string> = {
  studies_performed: 'Studies performed',
  adoption: 'Adoption %',
  sla_compliance: 'SLA compliance %',
};

const PERCENT_TREND_METRICS = new Set(['adoption', 'sla_compliance']);

export default function StatewideKPIs() {
  const user = authService.getUser();
  const [from, setFrom] = useState(defaultFromDate);
  const [to, setTo] = useState(localTodayIso);
  const [state, setState] = useState('all');
  const [district, setDistrict] = useState('all');
  const [facilityId, setFacilityId] = useState('all');
  const [facilityType, setFacilityType] = useState('all');
  const [modality, setModality] = useState('all');
  const [trendMetric, setTrendMetric] = useState('studies_performed');
  const [trendPeriod, setTrendPeriod] = useState('monthly');
  // Temporarily disabled — compare facility filters not working yet.
  // const [compareA, setCompareA] = useState('all');
  // const [compareB, setCompareB] = useState('all');
  const [drillFacilityId, setDrillFacilityId] = useState<string | undefined>();
  const [kpiKey, setKpiKey] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StatewideKpisData | null>(null);
  const [modalityOptions, setModalityOptions] = useState<string[]>([]);

  // const compareFacilityIds =
  //   compareA !== 'all' && compareB !== 'all' ? `${compareA},${compareB}` : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await getStatewideKpis({
      from,
      to,
      state,
      district,
      facilityId,
      facilityType,
      modality,
      trendMetric,
      trendPeriod,
      // compareFacilityIds,
      drillFacilityId,
      kpiKey,
    });
    if (result.success && result.data) {
      setData(result.data);
    } else {
      setError(result.error || 'Failed to load statewide KPIs');
      setData(null);
    }
    setLoading(false);
  }, [
    from,
    to,
    state,
    district,
    facilityId,
    facilityType,
    modality,
    trendMetric,
    trendPeriod,
    drillFacilityId,
    kpiKey,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const incoming = data?.filterOptions?.modalities || [];
    if (!incoming.length) {
      return;
    }
    setModalityOptions(prev => {
      const merged = new Set([...prev, ...incoming]);
      return [...merged].sort((a, b) => a.localeCompare(b));
    });
  }, [data?.filterOptions?.modalities]);

  const kpiItems: KpiCardItem[] = useMemo(() => {
    if (!data?.summary) {
      return [];
    }
    const s = data.summary;
    const connectedCount = s.connectedFacilities ?? s.onboardedFacilities;
    return [
      {
        key: 'totalFacilities',
        label: 'Total facilities',
        value: s.totalFacilities ?? 0,
        subtitle: `${formatNumber(s.activeFacilities ?? 0)} active`,
      },
      {
        key: 'adoption',
        label: 'Adoption',
        value: formatPercent(s.adoptionPercent ?? 0),
        subtitle:
          connectedCount == null ? undefined : `${formatNumber(connectedCount)} fully connected`,
        clickable: true,
        onClick: () => setKpiKey('adoption'),
      },
      { key: 'pendingOnboarding', label: 'Pending onboarding', value: s.pendingOnboarding ?? 0 },
      {
        key: 'totalStudies',
        label: 'Total studies',
        value: s.totalStudies ?? 0,
        subtitle: `${formatNumber(s.pendingStudies ?? 0)} pending`,
        clickable: true,
        onClick: () => setKpiKey('totalStudies'),
      },
      { key: 'reportsGenerated', label: 'Reports generated', value: s.reportsGenerated ?? 0 },
      {
        key: 'activeUsers',
        label: 'Active users',
        value: s.activeUsers ?? 0,
        subtitle: `${formatNumber(s.radiologistCount ?? 0)} radiologists · ${formatNumber(s.technicianCount ?? 0)} technicians`,
      },
      {
        key: 'averageTat',
        label: 'Average TAT',
        value: formatDurationMinutes(s.averageTatMinutes ?? 0),
        subtitle: s.criticalTatMinutes ? `Critical ${s.criticalTatMinutes}m` : undefined,
        variant: s.criticalTatMinutes ? 'critical-subtitle' : undefined,
      },
      {
        key: 'slaCompliance',
        label: 'SLA compliance',
        value: formatPercent(s.slaCompliancePercent ?? 0),
      },
      { key: 'completedStudies', label: 'Completed studies', value: s.completedStudies ?? 0 },
      { key: 'inactiveFacilities', label: 'Inactive facilities', value: s.inactiveFacilities ?? 0 },
    ];
  }, [data?.summary]);

  const handleClear = () => {
    setFrom(defaultFromDate());
    setTo(localTodayIso());
    setState('all');
    setDistrict('all');
    setFacilityId('all');
    setFacilityType('all');
    setModality('all');
    // setCompareA('all');
    // setCompareB('all');
  };

  const resetFacilityScopedFilters = () => {
    setFacilityId('all');
    // setCompareA('all');
    // setCompareB('all');
  };

  const resetGeoScopedFilters = () => {
    setDistrict('all');
    setFacilityType('all');
    resetFacilityScopedFilters();
  };

  const handleExport = async (format: AnalyticsExportFormat) => {
    if (format === 'pdf' && data) {
      const rows = data.facilityPerformance
        .map(
          r =>
            `<tr><td>${r.facilityName}</td><td>${r.status}</td><td>${r.studies}</td><td>${r.pending}</td><td>${r.adoptionPercent}%</td></tr>`
        )
        .join('');
      openAnalyticsPrintWindow(
        'Statewide KPI Dashboard',
        `<table><thead><tr><th>Facility</th><th>Status</th><th>Studies</th><th>Pending</th><th>Adoption</th></tr></thead><tbody>${rows}</tbody></table>`
      );
      return;
    }
    await exportStatewideKpis(
      { from, to, state, district, facilityId, facilityType, modality, trendMetric, trendPeriod },
      format === 'excel' ? 'csv' : format
    );
  };

  if (!canAccessPage(user, '/analytics/statewide-kpis')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  const trendData = (data?.trend || []).map(t => ({ label: t.period, value: t.value }));
  const modalityData = (data?.modalityWorkload || []).map(m => ({
    label: m.modality,
    value: m.count,
  }));

  return (
    <PageLayout
      title="Statewide KPIs"
      hideTitleInHeader
      breadcrumbs={[
        { label: 'Dashboard', path: '/admin-dashboard' },
        { label: 'Admin' },
        { label: 'Analytics' },
        { label: 'Ris' },
        { label: 'Statewide Kpis' },
      ]}
    >
      <div className="analytics-page">
        <div className="analytics-filter-bar">
          <div className="analytics-filter-field">
            <label>State</label>
            <select
              value={state}
              onChange={e => {
                setState(e.target.value);
                resetGeoScopedFilters();
              }}
            >
              <option value="all">All states</option>
              {(data?.filterOptions.states || []).map(s => (
                <option
                  key={s}
                  value={s}
                >
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>District</label>
            <select
              value={district}
              onChange={e => {
                setDistrict(e.target.value);
                setFacilityType('all');
                resetFacilityScopedFilters();
              }}
            >
              <option value="all">All districts</option>
              {(data?.filterOptions.districts || []).map(d => (
                <option
                  key={d}
                  value={d}
                >
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Facility</label>
            <select
              value={facilityId}
              onChange={e => setFacilityId(e.target.value)}
            >
              <option value="all">All facilities</option>
              {(data?.filterOptions.facilities || []).map(f => (
                <option
                  key={f.id}
                  value={String(f.id)}
                >
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Facility type</label>
            <select
              value={facilityType}
              onChange={e => {
                setFacilityType(e.target.value);
                resetFacilityScopedFilters();
              }}
            >
              <option value="all">All types</option>
              {(data?.filterOptions.facilityTypes || []).map(t => (
                <option
                  key={t}
                  value={t}
                >
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Modality</label>
            <select
              value={modality}
              onChange={e => setModality(e.target.value)}
            >
              <option value="all">All modalities</option>
              {(modalityOptions.length
                ? modalityOptions
                : data?.filterOptions?.modalities || []
              ).map(m => (
                <option
                  key={m}
                  value={m}
                >
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>From</label>
            <input
              type="date"
              value={from}
              max={to}
              onChange={e => setFrom(e.target.value)}
            />
          </div>
          <div className="analytics-filter-field">
            <label>To</label>
            <input
              type="date"
              value={to}
              min={from}
              max={localTodayIso()}
              onChange={e => setTo(e.target.value)}
            />
          </div>
          {/* Temporarily disabled — Compare facility A/B not working yet.
          <div className="analytics-filter-field">
            <label>Compare facility A</label>
            <select value={compareA} onChange={e => setCompareA(e.target.value)}>
              <option value="all">Select…</option>
              {(data?.filterOptions.facilities || []).map(f => (
                <option key={f.id} value={String(f.id)}>{f.name}</option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Compare facility B</label>
            <select value={compareB} onChange={e => setCompareB(e.target.value)}>
              <option value="all">Select…</option>
              {(data?.filterOptions.facilities || []).map(f => (
                <option key={f.id} value={String(f.id)}>{f.name}</option>
              ))}
            </select>
          </div>
          */}
          <div className="analytics-filter-actions">
            <button
              type="button"
              className="analytics-text-btn"
              onClick={handleClear}
            >
              Clear
            </button>
            <AnalyticsExportMenu
              onExport={handleExport}
              disabled={loading}
            />
          </div>
        </div>

        {error ? (
          <div className="analytics-error">
            {error}{' '}
            <button
              type="button"
              className="analytics-text-btn"
              onClick={load}
            >
              Retry
            </button>
          </div>
        ) : null}

        {loading ? (
          <AnalyticsDashboardSkeleton layout="statewide" />
        ) : (
          <>
            <KpiCardGrid
              items={kpiItems}
              layout="statewide"
            />

            <div className="analytics-charts-row analytics-charts-row--statewide">
              <AnalyticsLineChart
                title="Adoption & performance trend"
                data={trendData}
                tooltipValueLabel={TREND_METRIC_LABELS[trendMetric] || 'Value'}
                percentScale={PERCENT_TREND_METRICS.has(trendMetric)}
                controls={
                  <>
                    <select
                      value={trendMetric}
                      onChange={e => setTrendMetric(e.target.value)}
                    >
                      {TREND_METRIC_OPTIONS.map(option => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={trendPeriod}
                      onChange={e => setTrendPeriod(e.target.value)}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </>
                }
              />
              <AnalyticsVerticalBarChart
                title="Modality workload"
                data={modalityData}
              />
            </div>

            {/* Temporarily disabled with Compare facility A/B filters.
            {data?.compareMetrics?.length ? (
              <section className="analytics-section-card" style={{ marginBottom: 16 }}>
                <h3 className="analytics-section-title">Facility comparison</h3>
                <FacilityPerformanceTable rows={data.compareMetrics} onRowClick={id => setDrillFacilityId(String(id))} />
              </section>
            ) : null}
            */}

            {data?.drillDetail ? (
              <section
                className="analytics-section-card"
                style={{ marginBottom: 16 }}
              >
                <h3 className="analytics-section-title">
                  Facility detail — {data.drillDetail.facilityName}
                </h3>
                <p style={{ fontSize: 14, color: '#64748b' }}>
                  Studies: {data.drillDetail.studies} · Pending: {data.drillDetail.pending} ·
                  Adoption: {data.drillDetail.adoptionPercent}%
                </p>
              </section>
            ) : null}

            <FacilityPerformanceTable
              rows={data?.facilityPerformance || []}
              onRowClick={id => setDrillFacilityId(String(id))}
            />
          </>
        )}
      </div>
    </PageLayout>
  );
}
